import React, { useEffect } from 'react'
import { Row, Col, Card, Statistic, Typography, Spin, Alert } from 'antd'
import {
  UserOutlined,
  ShoppingCartOutlined,
  HomeOutlined,
  DollarOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  CarOutlined,
} from '@ant-design/icons'
import { useAppDispatch, useAppSelector } from '@store'
import { fetchDashboardData } from '@store/slices/dashboardSlice'

const { Title, Text } = Typography

const Dashboard: React.FC = () => {
  const dispatch = useAppDispatch()
  const { statistics, loading, error } = useAppSelector((state) => state.dashboard)

  useEffect(() => {
    dispatch(fetchDashboardData())
  }, [dispatch])

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size="large" />
        <div style={{ marginTop: '16px' }}>加载中...</div>
      </div>
    )
  }

  if (error) {
    return (
      <Alert
        message="数据加载失败"
        description={error}
        type="error"
        showIcon
        style={{ marginBottom: '24px' }}
      />
    )
  }

  return (
    <div>
      <div style={{ marginBottom: '24px' }}>
        <Title level={2} style={{ margin: 0 }}>
          仪表板
        </Title>
        <Text type="secondary">
          欢迎使用西舟菜园管理端，这里是您的业务概览
        </Text>
      </div>

      {/* 主要统计数据 */}
      <Row gutter={[24, 24]} style={{ marginBottom: '24px' }}>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="总用户数"
              value={statistics.totalUsers || 0}
              prefix={<UserOutlined />}
              valueStyle={{ color: '#3f8600' }}
            />
          </Card>
        </Col>
        
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="总订阅数"
              value={statistics.totalSubscriptions || 0}
              prefix={<ShoppingCartOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="总菜园数"
              value={statistics.totalGardens || 0}
              prefix={<HomeOutlined />}
              valueStyle={{ color: '#722ed1' }}
            />
          </Card>
        </Col>
        
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="总收入"
              value={statistics.totalRevenue || 0}
              prefix={<DollarOutlined />}
              precision={2}
              valueStyle={{ color: '#cf1322' }}
            />
          </Card>
        </Col>
      </Row>

      {/* 订阅状态统计 */}
      <Row gutter={[24, 24]} style={{ marginBottom: '24px' }}>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="进行中订阅"
              value={statistics.activeSubscriptions || 0}
              prefix={<CheckCircleOutlined />}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="已过期订阅"
              value={statistics.expiredSubscriptions || 0}
              prefix={<CloseCircleOutlined />}
              valueStyle={{ color: '#ff4d4f' }}
            />
          </Card>
        </Col>
        
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="已取消订阅"
              value={statistics.cancelledSubscriptions || 0}
              prefix={<CloseCircleOutlined />}
              valueStyle={{ color: '#d9d9d9' }}
            />
          </Card>
        </Col>
        
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="今日配送"
              value={statistics.todayDeliveries || 0}
              prefix={<CarOutlined />}
              valueStyle={{ color: '#fa8c16' }}
            />
          </Card>
        </Col>
      </Row>

      {/* 运营数据 */}
      <Row gutter={[24, 24]}>
        <Col xs={24} sm={12} lg={8}>
          <Card title="派单情况">
            <div style={{ marginBottom: '16px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                <span>未派单</span>
                <span style={{ fontWeight: 'bold', color: '#faad14' }}>
                  {statistics.undispatchedSubs ?? '-'}
                </span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                <span>已派单</span>
                <span style={{ fontWeight: 'bold', color: '#52c41a' }}>
                  {statistics.dispatchedSubs ?? '-'}
                </span>
              </div>
            </div>
          </Card>
        </Col>
        
        <Col xs={24} sm={12} lg={8}>
          <Card title="订阅率统计">
            <div style={{ marginBottom: '16px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                <span>活跃率</span>
                <span style={{ fontWeight: 'bold', color: '#52c41a' }}>
                  {statistics.totalSubscriptions > 0
                    ? `${((statistics.activeSubscriptions / statistics.totalSubscriptions) * 100).toFixed(1)}%`
                    : '0%'}
                </span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                <span>过期率</span>
                <span style={{ fontWeight: 'bold', color: '#ff4d4f' }}>
                  {statistics.totalSubscriptions > 0
                    ? `${((statistics.expiredSubscriptions / statistics.totalSubscriptions) * 100).toFixed(1)}%`
                    : '0%'}
                </span>
              </div>
            </div>
          </Card>
        </Col>
        
        <Col xs={24} sm={12} lg={8}>
          <Card title="系统状态">
            <div style={{ marginBottom: '16px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                <span>系统运行状态</span>
                <span style={{ color: '#3f8600' }}>正常</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                <span>数据同步状态</span>
                <span style={{ color: '#3f8600' }}>正常</span>
              </div>
            </div>
          </Card>
        </Col>
      </Row>
    </div>
  )
}

export default Dashboard
